/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/dashboardStats.scss';

import {AwesomeIcon, Loader} from '@dvrd/dvr-controls';

import {IconName, IconPrefix} from '@fortawesome/fontawesome-svg-core';

import classNames from 'classnames';

import React, {MouseEventHandler, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {AccessKey, AccountStats, AccountStatus} from '../../../util/interfaces';

import {AppContext} from '../../misc/appContext';

import {ApiIcon, getApiIcons} from '../../../util/dashboardUtil';

import shieldX16 from '@iconify/icons-octicon/shield-x-16';

import shieldSync from '@iconify/icons-mdi/shield-sync';

import shieldOff from '@iconify/icons-mdi/shield-off';

import shieldCheckFill from '@iconify/icons-ri/shield-check-fill';

import {Icon} from '@iconify/react';

import checkAll from '@iconify/icons-bi/check-all';

import pauseCircle from '@iconify/icons-feather/pause-circle';

import cloudArrowDownFill from '@iconify/icons-bi/cloud-arrow-down-fill';

import {parseThousandSeparator} from '../../../util/utils';

import useRight from '../../misc/useRight';

interface Props {
    onClickApiFlow: MouseEventHandler;
    onClickMailSettings: MouseEventHandler;
    onClickUpload: MouseEventHandler;
    stats: AccountStats;
    loading: boolean;
    emailsEnabled: boolean;
}

const icons = {
    [AccountStatus.ERROR]: shieldX16,
    [AccountStatus.IMPORTING]: shieldSync,
    [AccountStatus.IDLE]: shieldCheckFill,
    [AccountStatus.SETUP_API]: shieldOff
};

export default function DashboardStats(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const [canSeeStats, canAccessEmails, canAccessInvRequests, canSetupAPI] = useRight(
        AccessKey.INVOICE_STATS,
        AccessKey.PAGE_EMAIL,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.API_SETTINGS_SETUP
    );
    const {loading, stats, onClickMailSettings, onClickApiFlow, emailsEnabled, onClickUpload} = props;

    function getApiIcon() {
        const {account} = context.accountContext;
        if (!account) return null;
        const {status} = account;
        const icon = icons[status];
        if (!icon) return null;
        return <Icon icon={icon} className='stat-icon' />;
    }

    function getApiStatLabels() {
        const {account} = context.accountContext;
        if (!account) return null;
        const statusIcons = getApiIcons(account);
        if (emailsEnabled)
            statusIcons.push({
                labelClassName: classNames('auto-mail active', !canAccessEmails && 'no-click'),
                onClick: canAccessEmails ? onClickMailSettings : undefined,
                icon: <Icon icon={checkAll} className='label-icon check' />,
                iconLabel: t('dashboard.stats.autoMailOn', 'Automatisch mailen aan')
            });
        else
            statusIcons.push({
                labelClassName: classNames('auto-mail off', !canAccessEmails && 'no-click'),
                onClick: canAccessEmails ? onClickMailSettings : undefined,
                icon: <Icon icon={pauseCircle} className='label-icon' />,
                iconLabel: t('dashboard.stats.autoMailOff', 'Automatisch mailen UIT')
            });
        if (account.settings?.proactivelyUploadInvoices)
            statusIcons.push({
                labelClassName: classNames('auto-mail active', !canAccessInvRequests && 'no-click'),
                onClick: canAccessInvRequests ? onClickUpload : undefined,
                icon: <Icon icon={checkAll} className='label-icon check' />,
                iconLabel: t('dashboard.stats.autoUploadOn', 'Automatisch uploaden aan')
            });
        else
            statusIcons.push({
                labelClassName: classNames('auto-mail off', !canAccessInvRequests && 'no-click'),
                onClick: canAccessInvRequests ? onClickUpload : undefined,
                icon: <Icon icon={pauseCircle} className='label-icon' />,
                iconLabel: t('dashboard.stats.autoUploadOff', 'Auto uploaden UIT')
            });
        return statusIcons.map((icon: ApiIcon, index: number) => (
            <label key={index} className={classNames('stat-label', 'icon', icon.labelClassName)} onClick={icon.onClick}>
                {icon.icon} {icon.iconLabel}
            </label>
        ));
    }

    function renderStat(
        icon: React.ReactNode | IconName,
        value: number | string,
        totalValue: number | string,
        label: string,
        className: string,
        iconPrefix: IconPrefix = 'far'
    ) {
        if (!canSeeStats) {
            value = '-';
            totalValue = '-';
        }
        return (
            <div className='stat dashboard-stat'>
                <Loader active={loading} size='2.5rem' />
                <div className={classNames('icon-container', className)}>
                    {typeof icon === 'string' ? (
                        <AwesomeIcon name={icon as IconName} className='stat-icon' prefix={iconPrefix} />
                    ) : (
                        icon
                    )}
                </div>
                <label className='stat-label'>{label}</label>
                <label className='stat-value'>{parseThousandSeparator(totalValue)}</label>
                <label className='relative-value'>
                    <span className='green'>+{parseThousandSeparator(value)}</span>{' '}
                    {t('dashboard.stats.last30Days', 'AFGELOPEN 30 DAGEN')}
                </label>
            </div>
        );
    }

    function renderApiStat() {
        const {account} = context.accountContext;
        let rows: number = 4;
        if (account && [AccountStatus.ERROR, AccountStatus.SETUP_API].includes(account.status)) rows = 3;
        const isError = account && [AccountStatus.ERROR, AccountStatus.SETUP_API].includes(account.status);
        const clickable = isError && canSetupAPI;
        return (
            <div
                className={classNames('stat', 'api', isError && 'error')}
                onClick={clickable ? onClickApiFlow : undefined}
            >
                <Loader active={loading} />
                <div className={classNames('icon-container', account?.status)} style={{gridRowStart: `span ${rows}`}}>
                    {getApiIcon()}
                </div>
                {getApiStatLabels()}
            </div>
        );
    }

    return (
        <div className='stats dashboard-stats'>
            {renderStat(
                <Icon icon={cloudArrowDownFill} className='stat-icon' />,
                stats.order_count_thirty_days,
                stats.order_count,
                t('dashboard.stats.ordersReceived', 'AANTAL ORDERS ONTVANGEN'),
                'invoices',
                'far'
            )}
            {renderStat(
                'telegram-plane',
                stats.mail_count_thirty_days,
                stats.mail_count,
                t('dashboard.stats.mailsSent', 'AANTAL MAILS VERZONDEN'),
                'sent',
                'fab'
            )}
            {renderStat(
                'star',
                stats.review_count_thirty_days,
                `+${stats.review_count}`,
                t('dashboard.stats.reviewClicks', 'AANTAL REVIEW KLIKS'),
                'reviews',
                'fas'
            )}
            {renderApiStat()}
        </div>
    );
}
